import { useMemo } from 'react';
import {
  Content,
  Inline,
  InlineItem,
  Select,
  Stack,
  Strong,
  Text,
  UtilityButton,
} from '@kinesis/bungle';
import type { InventoryScopeRule, ScopeRuleDraft } from '@/types';
import {
  buildScopeRule,
  scopeRuleAxisLabels,
  scopeRuleAxisOptions,
  scopeRuleValue,
} from '@/data/scope-rules';

// One scope-rule rule card. The community analogue of suit's
// `scope-rule-card` + the per-type `rule-card-exclude-*` editors — but every
// one of the five axes is a simple enum `Select`, so a single card handles
// them all (the axis is fixed when the rule is added via the modal; the card
// edits its value). Outer chrome (border, delete button, spacing) matches
// suit's `ScopeRuleCard`.
type ScopeRuleCardProps = {
  rule: ScopeRuleDraft;
  disabled?: boolean;
  onChange: (specifier: InventoryScopeRule) => void;
  onRemove: () => void;
};

const ScopeRuleCard = (props: ScopeRuleCardProps) => {
  const { rule, disabled, onChange, onRemove } = props;
  const axis = rule.specifier.type;
  const options = useMemo(() => scopeRuleAxisOptions(axis), [axis]);

  const handleChange = (value: string) => {
    onChange(buildScopeRule(axis, value));
  };

  return (
    <Content border padding='small' paddingMode='equal'>
      <Inline>
        <InlineItem sizing='fill-container'>
          <Stack space='small'>
            <Text>
              Exclude activity related to{' '}
              <Strong>{scopeRuleAxisLabels[axis].toLowerCase()}</Strong>
            </Text>
            <Select
              search
              label={scopeRuleAxisLabels[axis]}
              overridePlaceholder={`Select a ${scopeRuleAxisLabels[
                axis
              ].toLowerCase()}`}
              onChange={handleChange}
              options={options}
              value={scopeRuleValue(rule.specifier)}
              valueKey='id'
              disabled={disabled}
            />
          </Stack>
        </InlineItem>
        <InlineItem sizing='fit-content'>
          <UtilityButton
            aria-label='Delete rule'
            icon='delete'
            variant='danger'
            onClick={onRemove}
            magnitude='small'
            disabled={disabled}
          />
        </InlineItem>
      </Inline>
    </Content>
  );
};

export { ScopeRuleCard };
export type { ScopeRuleCardProps };
